import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IContentControlStudioTimeline } from "../content-control-studio-timeline/i-content-control-studio-timeline";
import {
  ContentControlledEditingDirectiveViewModel,
} from 'app/js/src/domain/content-control/editing-directive';
import { ContentControlledKeyframe } from 'app/js/src/domain/content-control/keyframe';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';
import { ContentControlCanvasService } from 'app/js/src/infrastructure/content-control/content-control-canvas.service';
import { GetEnumNameByValue } from "app/js/src/domain/common/ui-utils";
import { ContentControlSubtitlesContainer } from 'app/js/src/domain/content-control/subtitles';
import { ETimelineMode } from 'app/js/src/domain/content-control/studio/e-timeline-mode';
import { IStudioViewModelContext } from "./i-studio-view-model-context";
import { StudioEventCoordinator } from "./studio-event-coordinator";
import { StudioSegmentController } from "./studio-segment-controller";
import { StudioAudioManager } from "./studio-audio-manager";
import { StudioTimelineZoomManager } from "./studio-timeline-zoom-manager";

export class StudioActionButtonDispatcher {
  constructor(
    private readonly _manager: IContentControlManager,
    private readonly canvasService: ContentControlCanvasService,
    private readonly _timeline: IContentControlStudioTimeline,
    private readonly _context: IStudioViewModelContext,
    private readonly _events: StudioEventCoordinator,
    private readonly _segments: StudioSegmentController,
    private readonly _audio: StudioAudioManager,
    private readonly _zoom: StudioTimelineZoomManager,
  ) {}

  getEventDefaultPosition(startTime: number): ContentControlledKeyframe {
    const { x, y, width, height } = this.canvasService.getEffectAreaDefaultPosition();
    return new ContentControlledKeyframe(startTime, x, y, width, height, true);
  }

  async applyToChildEvents(id: number, actionFunc: any): Promise<void> {
    const childEvents = this._segments.multiEventsSelectedIds.filter((x) => x != id);
    if (actionFunc && childEvents && childEvents.length > 0) {
      childEvents.forEach(async (childId) => {
        await actionFunc(this._segments.segments.find((x) => x.id == childId));
      });
    }
  }

  async actionButtonClicked(eventType: number): Promise<void> {
    const manager = this._manager;
    const timeline = this._timeline;
    const multiIds = this._segments.multiEventsSelectedIds;

    if (
      !manager.selectedSegment ||
      manager.selectedSegment.action === ContentControlledEventActionEnum.Cut ||
      (eventType === ContentControlledEventActionEnum.Cut && this._context.timelineMode === ETimelineMode.EditedTime)
    ) {
      return;
    }

    const tempSegment = JSON.parse(
      JSON.stringify(manager.selectedSegment),
    ) as ContentControlledEditingDirectiveViewModel;

    if (
      eventType === ContentControlledEventActionEnum.Approve &&
      manager.selectedSegment.isApproved == false &&
      manager.selectedSegmentIsEvent &&
      manager.selectedEvent.isMitigated == false
    ) {
      tempSegment.isApproved = true;
      if (multiIds.length > 1) {
        this.applyToChildEvents(tempSegment.id, async (x: ContentControlledEditingDirectiveViewModel) => {
          x.isApproved = true;
          await this._events.segmentToEvent(x, null, null, false);
        });
      }
      await this._events.segmentToEvent(tempSegment);
      return;
    }

    if (eventType !== ContentControlledEventActionEnum.Approve) {
      tempSegment.isMitigated = true;
      if (multiIds.length > 1) {
        this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
          x.isMitigated = true;
        });
      }
    }

    const currentTime = this._context.getVideoSource().currentPlayTimeAsClosestFrameMs(
      manager.selectedSegment.startsAtMs / 1000,
    );
    let kf: ContentControlledKeyframe = null;

    if (
      manager.selectedSegment.isConfigurationComplete &&
      manager.selectedSegment.action != eventType
    ) {
      const re = manager.reportableEvents.find((x) => x.id == manager.selectedSegment.id);
      if (re) manager.updateSelectedSegmentKeyframes([...re.keyframes]);
    }
    if (manager.selectedSegment.keyframes.length === 0) {
      kf = this.getEventDefaultPosition(currentTime);
    }

    tempSegment.title = GetEnumNameByValue(ContentControlledEventActionEnum, eventType, true);
    tempSegment.action = eventType;

    switch (eventType) {
      case ContentControlledEventActionEnum.Beep: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Audio;
        tempSegment.instructions = { beepFrequency: 440 };
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Audio;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
            x.instructions = tempSegment.instructions;
          });
        }
        await this._events.segmentToEvent(tempSegment, eventType, null);
        break;
      }
      case ContentControlledEventActionEnum.Mute: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Audio;
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Audio;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
          });
        }
        await this._events.segmentToEvent(tempSegment, eventType, null);
        break;
      }
      case ContentControlledEventActionEnum.BlackScreen: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Video;
        tempSegment.instructions = {};
        manager.selectedSegment.keyframes = [];
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Video;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
            x.keyframes = [];
          });
        }
        kf = new ContentControlledKeyframe(currentTime, 0, 0, 100, 100);
        await this._events.segmentToEvent(tempSegment, eventType, kf);
        break;
      }
      case ContentControlledEventActionEnum.PixelateScreen: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Video;
        tempSegment.instructions = { density: 50.0 };
        manager.selectedSegment.keyframes = [];
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Video;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
            x.instructions = { density: 50.0 };
          });
        }
        kf = new ContentControlledKeyframe(currentTime, 0, 0, 100, 100);
        await this._events.segmentToEvent(tempSegment, eventType, kf);
        break;
      }
      case ContentControlledEventActionEnum.BlurScreen: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Video;
        tempSegment.instructions = { sampleDistance: 5.0 };
        manager.selectedSegment.keyframes = [];
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Video;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
            x.instructions = { sampleDistance: 5.0 };
          });
        }
        kf = new ContentControlledKeyframe(currentTime, 0, 0, 100, 100);
        await this._events.segmentToEvent(tempSegment, eventType, kf);
        break;
      }
      case ContentControlledEventActionEnum.BlackRect:
      case ContentControlledEventActionEnum.BlackOval: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Video;
        tempSegment.instructions = {};
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Video;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
          });
        }
        await this._events.segmentToEvent(tempSegment, eventType, kf);
        break;
      }
      case ContentControlledEventActionEnum.BlurRect:
      case ContentControlledEventActionEnum.BlurOval: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Video;
        tempSegment.instructions = { sampleDistance: 5.0 };
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Video;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
            x.instructions = { sampleDistance: 5.0 };
          });
        }
        await this._events.segmentToEvent(tempSegment, eventType, kf);
        break;
      }
      case ContentControlledEventActionEnum.PixelateRect:
      case ContentControlledEventActionEnum.PixelateOval: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Video;
        tempSegment.instructions = { density: 35.0 };
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Video;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
            x.instructions = { density: 35.0 };
          });
        }
        await this._events.segmentToEvent(tempSegment, eventType, kf);
        break;
      }
      case ContentControlledEventActionEnum.Cut: {
        kf = new ContentControlledKeyframe(currentTime, 0, 0, 100, 100);
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Inclusive;
        manager.selectedSegment.keyframes = [];
        await this._events.segmentToEvent(tempSegment, eventType, kf);
        break;
      }
      case ContentControlledEventActionEnum.EditSubtitles: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Subtitles;
        manager.selectedSegment.keyframes = [];
        tempSegment.subtitles = new ContentControlSubtitlesContainer();
        (this._audio?.subtitleOptions ?? []).map((x) =>
          tempSegment.subtitles.addSubtitleSegments(
            x,
            manager.getSubtitleSegments(x, tempSegment.startsAtMs, tempSegment.endsAtMs),
          ),
        );
        this._segments.segments.find((x) => x.id == tempSegment.id).subtitles =
          tempSegment.subtitles.clone();
        await this._events.segmentToEvent(
          tempSegment,
          eventType,
          new ContentControlledKeyframe(currentTime, 0, 0, 100, 100),
        );
        break;
      }
      case ContentControlledEventActionEnum.RemoveSubtitles: {
        tempSegment.mediaType = ContentControlledMediaTypeEnum.Subtitles;
        manager.selectedSegment.keyframes = [];
        if (multiIds.length > 1) {
          this.applyToChildEvents(tempSegment.id, (x: ContentControlledEditingDirectiveViewModel) => {
            x.mediaType = ContentControlledMediaTypeEnum.Inclusive;
            x.title = tempSegment.title;
            x.action = tempSegment.action;
            x.keyframes = [];
          });
        }
        kf = new ContentControlledKeyframe(currentTime, 0, 0, 100, 100);
        await this._events.segmentToEvent(tempSegment, eventType, kf);
        // falls through to default intentionally
      }
      default:
        break;
    }

    if (multiIds.length > 1) {
      this.applyToChildEvents(
        tempSegment.id,
        async (x: ContentControlledEditingDirectiveViewModel) => {
          await this._events.segmentToEvent(x, eventType, kf, false);
        },
      );
    }
    setTimeout(() => this._context.markForCheck(), 0);
  }
}
